import {Component, OnDestroy, OnInit} from "@angular/core";
import {TiposPreguntasService} from "../../tipos-preguntas.service";
import {ItemPreguntaDTO, PreguntaDTO} from "@DTOS";
import {CommandAsync, ICommand} from "@nahuelmorata/ngx-command";
import {
    EditorCondicionesComponent
} from "../../../../encuestas-detalle/editor-condiciones/editor-condiciones.component";
import {ItemPreguntaModelDTO, PreguntaModelDTO} from "@Models";
import {EncuestasService} from "../../../../encuestas.service";
import {ModalService, NotificacionServiceEmisor} from "@shared/services";
import {
    obtenerIdPreguntaTipo,
    obtenerNombresPreguntaSubtipo,
    PreguntasTipoEnum,
    Subtipo,
    TiposConSubtipo
} from "@shared/enums";
import {ConfiguracionOpcionesModalInput} from "@shared/components";
import {Nilable} from "@shared/utils";
import {debounceTime, filter, map, mergeMap, tap} from "rxjs/operators";
import {Entrada} from "@shared/interfaces";
import { Subscription} from "rxjs";
import {ModalAccionesItemComponent} from "@shared/components/modal-acciones-item/modal-acciones-item.component";
import {TipoPreguntaComponent} from "../tipo-pregunta/tipo-pregunta.component";


@Component({
    selector: "app-tipo-pregunta-single",
    templateUrl: "./tipo-pregunta-single.component.html",
    styleUrls: ["./tipo-pregunta-single.component.scss"]
})
export class TipoPreguntaSingleComponent extends TipoPreguntaComponent implements OnInit, OnDestroy {
    public pregunta: PreguntaModelDTO;
    public items: ItemPreguntaModelDTO[] = [];
    public subtipos: Entrada<Subtipo>[] = [];

    public textoItem = "";
    public colores = {};
    public esMultiple = false;
    public alineacionMap = new Map<number, Nilable<"T" | "B">>();

    public agregarCmd: ICommand = new CommandAsync(() => this.agregarItem());
    public borrarCmd: ICommand = new CommandAsync((item: ItemPreguntaModelDTO) =>
        this.borrarItem(item));
    public abrirModalSiguientePreguntaCmd: ICommand =
        new CommandAsync((item: ItemPreguntaModelDTO) => this.abrirModalSiguientePregunta(item));
    public abrirAccionesCmd: ICommand = new CommandAsync((item: ItemPreguntaModelDTO) => this.abrirAccionesItem(item));

    private subsPregunta = new Subscription();
    private subsItems = new Subscription();
    private subsAcciones: Nilable<Subscription>;

    constructor(private tiposPreguntasService: TiposPreguntasService, private modalService: ModalService,
        private encuestasService: EncuestasService, private notificacionService: NotificacionServiceEmisor) {
        super();
    }

    ngOnInit() {
        this.pregunta = this.tiposPreguntasService.preguntaModel;
        this.colores = this.tiposPreguntasService.colores;
        this.subtipos = obtenerNombresPreguntaSubtipo(this.pregunta.Tipo as TiposConSubtipo).map(([tipo, nombre]) => ({
            clave: tipo,
            valor: nombre
        } as Entrada<Subtipo>));
        this.esMultiple = this.pregunta.Tipo === PreguntasTipoEnum.MULTIPLE_CHOICE;
        this.cargarItemsModels();
    }

    ngOnDestroy() {
        this.subsPregunta.unsubscribe();
        this.subsItems.unsubscribe();
        this.subsAcciones?.unsubscribe();
    }

    public async agregarItem(): Promise<void> {
        let itemPreguntaDTO = new ItemPreguntaDTO({
            text: this.textoItem.trim()
        });

        this.textoItem = "";
        itemPreguntaDTO = await this.tiposPreguntasService.agregarItemPregunta(this.pregunta.Entidad, itemPreguntaDTO);
        const model = this.crearItemModel(itemPreguntaDTO);
        this.items.push(model);
        this.notificacionService.notificarExito("Item agregado correctamente.");
    }

    public async borrarItem(item: ItemPreguntaModelDTO): Promise<void> {
        await this.tiposPreguntasService.borrarItemPregunta(this.pregunta.Entidad, item.Entidad);
        this.subsItems.unsubscribe();
        this.subsItems = new Subscription();
        this.cargarItemsModels();
        this.notificacionService.notificarExito("Item borrado correctamente.");
    }

    public abrirEditorCondiciones(item: ItemPreguntaModelDTO) {
        if (this.accesoPublico) {
            return;
        }
        const modalRef = this.modalService.open(EditorCondicionesComponent, {
            centered: true,
            windowClass: "modal-condiciones"
        });
        const instancia = modalRef.componentInstance;
        instancia.item = item;
        instancia.encuesta = this.tiposPreguntasService.encuesta;
    }

    public obtenerId(tipoPregunta: PreguntasTipoEnum) {
        return obtenerIdPreguntaTipo(tipoPregunta);
    }

    public cambiarAlineacion(item: ItemPreguntaModelDTO, event: any, alineacion: Nilable<"T" | "B">) {
        if (event.target.checked) {
            this.alineacionMap.set(item.Id, alineacion);
            item.Alineacion = alineacion;
        } else {
            this.alineacionMap.set(item.Id, null);
            item.Alineacion = null;
        }
    }

    private esPreguntaSingle(): boolean {
        return this.pregunta.Tipo === PreguntasTipoEnum.SINGLE_CHOICE;
    }

    private async abrirModalSiguientePregunta(item: ItemPreguntaModelDTO): Promise<void> {
        const preguntaDTO = this.tiposPreguntasService.encuesta.preguntas?.find(pregunta =>
            pregunta.id === item.PreguntaSiguiente) ?? null;
        const configuracionOpciones: ConfiguracionOpcionesModalInput<Nilable<PreguntaDTO>> = {
            items: this.tiposPreguntasService.encuesta.preguntas ?? [],
            label: "texto",
            item: preguntaDTO,
            clear: false,
            multiple: false
        };

        const preguntaSiguiente: Nilable<PreguntaDTO> =
            await this.modalService.openModalInputOpciones(configuracionOpciones, { centered: true });

        if (preguntaSiguiente == null) {
            return;
        }

        const pasoControles = TipoPreguntaSingleComponent.controlesPreguntaSiguiente(preguntaSiguiente, item);
        if (!pasoControles) {
            return;
        }

        if (this.esPreguntaSingle()) {
            await this.cambiarSiguienteEnItem(preguntaSiguiente, item);
        } else {
            await this.preguntarYCambiarSiguienteEnPregunta(preguntaSiguiente);
        }
    }

    private async cambiarSiguienteEnItem(preguntaSiguiente: PreguntaDTO, item: ItemPreguntaModelDTO): Promise<void> {
        this.notificacionService.notificarInfo("Actualizando item...");
        item.PreguntaSiguiente = preguntaSiguiente.id;
        await this.encuestasService.guardarItemPregunta(
            this.tiposPreguntasService.encuesta, this.pregunta.Entidad, item.Entidad
        );
        this.notificacionService.notificarInfo("Item actualizado.");
    }

    private async preguntarYCambiarSiguienteEnPregunta(preguntaSiguiente: PreguntaDTO): Promise<void> {
        const ok = await this.modalService.openModalDecision("Esta seguro que quiere cambiar en todos los items la siguiente pregunta?");

        if (!ok) {
            return;
        }
        this.pregunta.SiguientePregunta = preguntaSiguiente.id;
        await this.encuestasService.guardarPregunta(this.tiposPreguntasService.encuesta, this.pregunta.Entidad);

        await Promise.all(this.pregunta.Items.map(async itemDTO => {
            itemDTO.preguntaSiguiente = preguntaSiguiente.id;

            await this.encuestasService.guardarItemPregunta(
                this.tiposPreguntasService.encuesta, this.pregunta.Entidad, itemDTO
            );
        }));
        this.notificacionService.notificarInfo("Items actualizados.");
    }

    private static controlesPreguntaSiguiente(preguntaSiguiente: PreguntaDTO, item: ItemPreguntaModelDTO): boolean {
        if (preguntaSiguiente == null) {
            return false;
        }

        return preguntaSiguiente.id !== item.PreguntaSiguiente;
    }

    private cargarItemsModels() {
        this.items = this.pregunta.Items.map(item => {
            this.alineacionMap.set(item.id, item.alineacion);
            return this.crearItemModel(item);
        });
    }

    private crearItemModel(item: ItemPreguntaDTO) {
        const model = new ItemPreguntaModelDTO(item);
        this.subsItems.add(
            model.changed$.pipe(
                filter(Boolean),
                filter(() => item.texto !== ""),
                debounceTime(400),
                mergeMap(() => this.tiposPreguntasService.guardarItemPregunta(this.pregunta.Entidad, item)),
                tap(() => this.notificacionService.notificarExito("Item modificado correctamente."))
            ).subscribe()
        );
        return model;
    }

    private async abrirAccionesItem(item: ItemPreguntaModelDTO) {
        const modalRef = this.modalService.open(ModalAccionesItemComponent, { centered: true });
        const instancia: ModalAccionesItemComponent = modalRef.componentInstance;
        await instancia.cargarItem(item);
        this.subsAcciones = new Subscription();
        this.subsAcciones.add(
            instancia.guardarAccionItem$.pipe(
                mergeMap(accion => this.encuestasService.agregarAccionItem(
                    this.tiposPreguntasService.encuesta, this.pregunta.Entidad, item.Entidad, accion
                ))
            ).subscribe(async accion => {
                item.Acciones.push(accion);
                instancia.acciones = [...item.Acciones];
            })
        );
        this.subsAcciones.add(
            instancia.borrarAccionItem$.pipe(
                mergeMap(accion => this.encuestasService.borrarAccionItem(
                    this.tiposPreguntasService.encuesta, this.pregunta.Entidad, item.Entidad, accion
                ).pipe(
                    map(() => accion)
                ))
            ).subscribe(accionBorrar => {
                item.Entidad.acciones = item.Acciones.filter(accion => accion.id !== accionBorrar.id);
                instancia.acciones = [...item.Acciones];

            })
        );
        await modalRef.result.catch(() => null);
        this.subsAcciones.unsubscribe();
        this.subsAcciones = null;
    }
}
